import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { isNative } from "../mobileAuth";
import { Page, Logo } from "../components/Layout";
import { CheckCircle2, XCircle } from "lucide-react";

const TYPE_LABEL = { open_space: "Open Space", cabina: "Cabina Privacy" };

// Bridge iOS: se la pagina di ritorno HTTPS è aperta nel browser di sistema
// (SFSafariViewController, quindi NON nella WebView dell'app → isNative()===false) e la URL
// porta il marker native=1, reindirizza allo schema masstudio:// per far uscire iOS dal
// browser e riaprire l'app. La pagina HTTPS resta come fallback se lo schema non apre l'app.
function tryNativeBridge(params, kind) {
  try {
    if (params.get("native") !== "1") return;
    if (isNative()) return; // già dentro la WebView dell'app: nessun bridge (anti-loop)
    const p = new URLSearchParams(params);
    p.delete("native"); // niente native nella URL interna finale (anti-loop)
    const qs = p.toString();
    window.location.replace(`masstudio://payment/${kind}${qs ? `?${qs}` : ""}`);
  } catch (e) {
    /* fallback: resta la pagina HTTPS visibile/usabile */
  }
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const hasPending = (() => { try { return !!localStorage.getItem("mas_pending_booking"); } catch { return false; } })();

  useEffect(() => { tryNativeBridge(params, "success"); }, [params]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setInfo(data); setStatus("paid"); return; }
        if (data.status === "expired" || tries > 8) { setStatus("error"); return; }
      } catch { if (tries > 8) { setStatus("error"); return; } }
      tries++; setTimeout(poll, 2000);
    };
    poll();
  }, [params]);

  const isReschedule = info?.payment_type === "LATE_RESCHEDULE";
  const isVetrina = info?.payment_type === "VETRINA_MESSAGE";
  const isCourse = info?.payment_type === "COURSE" || info?.kind === "course_enroll";

  return (
    <Page>
      <div className="text-center pt-16">
        <Logo className="inline-block" sub />
        {status === "checking" && <p className="text-gray-500 mt-8">Verifica del pagamento in corso…</p>}

        {status === "paid" && isReschedule && <>
          <CheckCircle2 size={56} className="mx-auto mt-8 text-green-600" />
          <h1 className="serif text-2xl mt-4" data-testid="reschedule-success-title">Prenotazione spostata con successo</h1>
          <div className="text-gray-600 mt-3 space-y-1">
            <div>Nuova data: <b className="text-gray-900">{info?.new_date}</b></div>
            <div>Postazione: <b className="text-gray-900">{TYPE_LABEL[info?.station_type] || info?.station_type}</b></div>
            <div>Importo pagato: <b className="text-gray-900">{info?.amount ? `${info.amount} €` : "15 €"}</b></div>
          </div>
          <button data-testid="resched-done" onClick={() => nav("/profilo")} className="btn-primary mt-6">Le mie prenotazioni</button>
        </>}

        {status === "paid" && isVetrina && <>
          <CheckCircle2 size={56} className="mx-auto mt-8 text-green-600" />
          <h1 className="serif text-2xl mt-4" data-testid="vetrina-success-title">Pagamento completato</h1>
          <p className="text-gray-600 mt-2">Il tuo messaggio è stato pubblicato in Vetrina.</p>
          <button data-testid="vetrina-done" onClick={() => nav("/")} className="btn-primary mt-6">Torna alla Home</button>
        </>}

        {status === "paid" && isCourse && <>
          <CheckCircle2 size={56} className="mx-auto mt-8 text-green-600" />
          <h1 className="serif text-2xl mt-4" data-testid="course-success-title">Pagamento completato</h1>
          <p className="text-gray-600 mt-2">La tua iscrizione al corso è confermata.</p>
          <div className="mt-6 flex flex-col gap-3 items-center">
            <button data-testid="course-back-courses" onClick={() => nav("/corsi")} className="btn-primary">Torna ai corsi</button>
            <button data-testid="course-see-enrollment" onClick={() => nav(info?.course_id ? `/corsi/${info.course_id}` : "/profilo")} className="btn-outline">Vedi la tua iscrizione</button>
          </div>
        </>}

        {status === "paid" && !isReschedule && !isVetrina && !isCourse && <>
          <CheckCircle2 size={56} className="mx-auto mt-8 text-green-600" />
          <h1 className="serif text-2xl mt-4">Pagamento completato</h1>
          <p className="text-gray-600 mt-2">Le giornate sono state caricate sul tuo profilo.</p>
          {hasPending ? (
            <div className="mt-6 flex flex-col gap-3 items-center">
              <button data-testid="pay-back-booking" onClick={() => nav("/prenota")} className="btn-primary">Torna alla prenotazione</button>
              <button data-testid="pay-done" onClick={() => nav("/profilo")} className="btn-outline">Vai al profilo</button>
            </div>
          ) : (
            <button data-testid="pay-done" onClick={() => nav("/profilo")} className="btn-primary mt-6">Vai al profilo</button>
          )}
        </>}

        {status === "error" && <>
          <XCircle size={56} className="mx-auto mt-8 text-red-500" />
          <p className="text-gray-600 mt-4">Non è stato possibile confermare il pagamento.</p>
          <button onClick={() => nav("/profilo")} className="btn-outline mt-6">Torna al profilo</button>
        </>}
      </div>
    </Page>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const isReschedule = params.get("reschedule") === "1";
  const vetrinaMid = params.get("vetrina");
  const sid = params.get("session_id");

  useEffect(() => { tryNativeBridge(params, "cancel"); }, [params]);

  useEffect(() => {
    // Rilascia l'eventuale hold sulla nuova data: la prenotazione originale resta valida.
    if (isReschedule && sid) { api.post(`/payments/${sid}/abort-reschedule`).catch(() => {}); }
    if (vetrinaMid) { api.post(`/vetrina/${vetrinaMid}/abort`).catch(() => {}); }
  }, [isReschedule, sid, vetrinaMid]);

  return (
    <Page>
      <div className="text-center pt-20">
        <XCircle size={56} className="mx-auto text-gray-400" />
        <h1 className="serif text-2xl mt-4">Pagamento annullato</h1>
        {isReschedule ? (
          <>
            <p className="text-gray-600 mt-3">Il pagamento non è stato completato. La tua prenotazione originale non è stata modificata.</p>
            <button data-testid="pay-retry" onClick={() => nav("/profilo")} className="btn-primary mt-6">Le mie prenotazioni</button>
          </>
        ) : vetrinaMid ? (
          <>
            <p className="text-gray-600 mt-3">Pagamento non completato. Il messaggio non è stato pubblicato.</p>
            <button data-testid="pay-retry" onClick={() => nav("/flash/led")} className="btn-primary mt-6">Riprova</button>
          </>
        ) : (
          <button data-testid="pay-retry" onClick={() => nav("/prenota")} className="btn-primary mt-6">Torna ai pacchetti</button>
        )}
      </div>
    </Page>
  );
}
